import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pushEvent } from '@/hooks/useAnalytics';
import { galleryMockData, toolTypeFilters, type GalleryItem } from './data/galleryData';
import GalleryImageCard from './components/GalleryImageCard';
import GalleryDetailModal from './components/GalleryDetailModal';

export default function GalleryPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [items, setItems] = useState<GalleryItem[]>(() => galleryMockData);
  const [showDetail, setShowDetail] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const displayItems = useMemo(
    () => (activeFilter === 'all' ? items : items.filter((item) => item.toolType === activeFilter)),
    [activeFilter, items],
  );

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeFilter]);

  const handleItemClick = (item: GalleryItem) => {
    pushEvent('gallery_item_click', undefined, { itemId: item.id, toolType: item.toolType });
    setSelectedItem(item);
    setShowDetail(true);
  };

  const handleLike = (id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, liked: !item.liked, likes: item.liked ? item.likes - 1 : item.likes + 1 }
          : item,
      ),
    );
    if (selectedItem?.id === id) {
      setSelectedItem((prev) =>
        prev
          ? { ...prev, liked: !prev.liked, likes: prev.liked ? prev.likes - 1 : prev.likes + 1 }
          : null,
      );
    }
    pushEvent('gallery_like_click', undefined, { itemId: id });
  };

  const handleUsePrompt = (prompt: string) => {
    pushEvent('gallery_use_prompt_click', undefined);
    navigate(`/generate?prompt=${encodeURIComponent(prompt)}`);
  };

  return (
    <div
      className="min-h-dvh font-nunito flex flex-col relative overflow-hidden max-w-md mx-auto w-full"
      style={{ background: 'linear-gradient(160deg, #FFF5F9 0%, #F8F3FF 50%, #F3F6FF 100%)' }}
    >
      {/* Background blobs */}
      <div
        className="absolute top-0 right-0 w-56 h-56 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #FF6B9D22 0%, transparent 70%)',
          transform: 'translate(20%, -20%)',
        }}
      />
      <div
        className="absolute top-48 left-0 w-40 h-40 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #A855F718 0%, transparent 70%)',
          transform: 'translate(-30%, 0)',
        }}
      />

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-5 pt-safe pb-3">
        <button
          onClick={() => navigate('/')}
          className="w-10 h-10 flex items-center justify-center rounded-2xl cursor-pointer transition-all active:scale-95"
          style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
        >
          <i className="ri-arrow-left-line text-gray-700 text-lg"></i>
        </button>
        <h1 className="text-lg font-bold text-gray-800">{t('gallery_title')}</h1>
        <div className="w-10" />
      </div>

      {/* Filter tabs */}
      <div className="relative z-10 px-5 mb-3">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
          {toolTypeFilters.map((filter) => {
            const isActive = activeFilter === filter.id;
            return (
              <button
                key={filter.id}
                onClick={() => {
                  setActiveFilter(filter.id);
                  pushEvent('gallery_filter_click', undefined, { filterId: filter.id });
                }}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-300 active:scale-95 ${
                  isActive ? 'text-white shadow-md' : 'text-gray-500 bg-white/70 hover:bg-white'
                }`}
                style={
                  isActive
                    ? { background: filter.color, boxShadow: `0 4px 12px ${filter.color}40` }
                    : { border: '1px solid rgba(0,0,0,0.06)' }
                }
              >
                {t(filter.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats bar */}
      <div className="relative z-10 px-5 mb-3">
        <div
          className="flex items-center justify-between px-4 py-2.5 rounded-2xl"
          style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,107,157,0.08)' }}
        >
          <div className="flex items-center gap-1.5">
            <i className="ri-image-line text-pink-400 text-sm"></i>
            <span className="text-xs text-gray-500">
              {t('gallery_totalWorks')}: <span className="font-semibold text-gray-700">{displayItems.length}</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <i className="ri-fire-line text-orange-400 text-sm"></i>
            <span className="text-xs text-gray-500">
              {t('gallery_hotToday')}: <span className="font-semibold text-gray-700">{displayItems.length > 0 ? `${(displayItems.length * 3).toLocaleString()}` : '—'}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Gallery grid */}
      <div
        ref={scrollRef}
        className="relative z-10 flex-1 overflow-y-auto ios-scroll px-4"
        style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}
      >
        {displayItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(255,107,157,0.08)' }}>
              <i className="ri-image-line text-pink-300 text-2xl"></i>
            </div>
            <p className="text-sm text-gray-400">{t('gallery_empty')}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2.5">
              {displayItems.map((item, index) => (
                <div key={item.id} className={index % 3 === 0 ? 'col-span-2' : 'col-span-1'}>
                  <GalleryImageCard
                    item={item}
                    index={index}
                    onClick={() => handleItemClick(item)}
                    onLike={() => handleLike(item.id)}
                  />
                </div>
              ))}
            </div>

            {/* Load more */}
            <div className="flex items-center justify-center py-6">
              <span className="text-xs text-gray-400">{t('gallery_noMore')}</span>
            </div>
          </>
        )}
      </div>

      {/* Detail modal */}
      {showDetail && selectedItem && (
        <GalleryDetailModal
          item={selectedItem}
          onClose={() => setShowDetail(false)}
          onLike={() => handleLike(selectedItem.id)}
          onUsePrompt={handleUsePrompt}
        />
      )}
    </div>
  );
}
